/* extrato.css — direção "Extrato" do FinApp v2.
   Cinza com viés verde no fundo (tinted neutral), verde garrafa no acento,
   vermelho tijolo no negativo. Ver FinApp/estudos/app-financeiro-fork-actual-2026-09-05.md §4.

   Gerado a partir de light.css: cada --palette-<family><shade> foi substituído por um
   valor derivado da identidade Extrato (rampa própria por HSL, ancorada nos hexes exatos
   do brief), preservando a estrutura de contraste/hierarquia do tema original. As chaves
   explicitamente especificadas no brief (pageBackground*, pageText*, card*, table*,
   number*) têm prioridade e usam os valores literais do brief. */
:root {
  --color-pageBackground: #f4f6f5;
  --color-pageBackgroundModalActive: #eef1f0;
  --color-pageBackgroundTopLeft: #f4f6f5;
  --color-pageBackgroundBottomRight: #eef1f0;
  --color-pageBackgroundLineTop: #ffffff;
  --color-pageBackgroundLineMid: #f4f6f5;
  --color-pageBackgroundLineBottom: #eef1f0;
  --color-pageText: #16211d;
  --color-pageTextLight: #6b7a75;
  --color-pageTextSubdued: #9aa8a3;
  --color-pageTextDark: #0d1512;
  --color-pageTextPositive: #0f6b4f;
  --color-pageTextLink: #0f6b4f;
  --color-pageTextLinkLight: #3f8f74;

  --color-cardBackground: #ffffff;
  --color-cardBorder: #e3e8e6;
  --color-cardShadow: transparent;

  --color-tableBackground: #ffffff;
  --color-tableRowBackgroundAlternate: var(--color-tableBackground);
  --color-tableRowBackgroundHover: #f7f8f8;
  --color-tableText: #16211d;
  --color-tableTextItemAdded: var(--color-tableText);
  --color-tableTextLight: #83938d;
  --color-tableTextSubdued: #f4f6f5;
  --color-tableTextSelected: #24342e;
  --color-tableTextHover: #070d0b;
  --color-tableTextInactive: #6b7a74;
  --color-tableHeaderText: #6b7a75;
  --color-tableHeaderBackground: #ffffff;
  --color-tableBorder: #e3e8e6;
  --color-tableBorderSelected: #1c7b5e;
  --color-tableBorderHover: #3f8f77;
  --color-tableBorderSeparator: #83938d;
  --color-tableRowBackgroundHighlight: #aec6bf;
  --color-tableRowBackgroundHighlightText: #24342e;
  --color-tableRowHeaderBackground: #f7f8f8;
  --color-tableRowHeaderText: #0d1512;

  --color-numberPositive: #0f6b4f;
  --color-numberNegative: #a93226;
  --color-numberNeutral: #16211d;
  --color-budgetNumberNegative: var(--color-numberNegative);
  --color-budgetNumberZero: var(--color-tableTextSubdued);
  --color-budgetNumberNeutral: var(--color-tableText);
  --color-budgetNumberPositive: var(--color-budgetNumberNeutral);
  --color-templateNumberFunded: var(--color-numberPositive);
  --color-templateNumberUnderFunded: #b07d2b;
  --color-toBudgetPositive: var(--color-numberPositive);
  --color-toBudgetZero: var(--color-numberPositive);
  --color-toBudgetNegative: var(--color-budgetNumberNegative);

  /*
   * ⭐⭐ BARRA LATERAL CLARA — medida no MULTICAP (o app que o dono mandou
   * copiar) em 14/09/2026. Ela era quase preta (#070d0b) e essa era a maior
   * diferença visual entre os dois: no MULTICAP a barra é branca, com o item
   * selecionado marcado por um fundo cinza suave, não por inversão de tema.
   */
  --color-sidebarBackground: #ffffff;
  --color-sidebarItemBackgroundPending: #fdf1dd;
  --color-sidebarItemBackgroundPositive: #e8f5ee;
  --color-sidebarItemBackgroundFailed: #fdeaea;
  --color-sidebarItemBackgroundHover: #f4f4f6;
  --color-sidebarItemAccentSelected: #15654a;
  --color-sidebarItemText: #4b5563;
  --color-sidebarItemTextUpdated: var(--color-sidebarItemText);
  --color-sidebarItemTextSelected: #15654a;
  --color-sidebarBudgetName: #111827;

  --color-menuBackground: #ffffff;
  --color-menuItemBackground: #f7f8f8;
  --color-menuItemBackgroundHover: #f4f6f5;
  --color-menuItemText: #070d0b;
  --color-menuItemTextHover: var(--color-menuItemText);
  --color-menuItemTextSelected: #5ea590;
  --color-menuItemTextHeader: #83938d;
  --color-menuBorder: #f4f6f5;
  --color-menuBorderHover: #eaf0ee;
  --color-menuKeybindingText: #83938d;
  --color-menuAutoCompleteBackground: #070d0b;
  --color-menuAutoCompleteBackgroundHover: #445850;
  --color-menuAutoCompleteText: #ffffff;
  --color-menuAutoCompleteTextHover: #bacec8;
  --color-menuAutoCompleteTextHeader: #eee8dd;
  --color-menuAutoCompleteItemTextHover: var(--color-menuAutoCompleteText);
  --color-menuAutoCompleteItemText: var(--color-menuAutoCompleteText);

  --color-modalBackground: #ffffff;
  --color-modalBorder: #ffffff;
  --color-mobileHeaderBackground: #3f8f77;
  --color-mobileHeaderText: #f7f8f8;
  --color-mobileHeaderTextSubdued: #d9dfdd;
  --color-mobileHeaderTextHover: rgba(200, 200, 200, 0.15);
  --color-mobilePageBackground: #f7f8f8;
  --color-mobileNavBackground: #ffffff;
  --color-mobileNavItem: #8b9b95;
  --color-mobileNavItemSelected: #1c7b5e;
  --color-mobileAccountShadow: #9aa8a3;
  --color-mobileAccountText: #105741;
  --color-mobileTransactionSelected: #1c7b5e;

  --color-mobileViewTheme: var(--color-mobileHeaderBackground);
  --color-mobileConfigServerViewTheme: #1c7b5e;

  --color-markdownNormal: #c1d3cd;
  --color-markdownDark: #3f8f77;
  --color-markdownLight: #eaf0ee;

  --color-buttonMenuText: #f4f6f5;
  --color-buttonMenuTextHover: #f7f8f8;
  --color-buttonMenuBackground: transparent;
  --color-buttonMenuBackgroundHover: rgba(200, 200, 200, 0.25);
  --color-buttonMenuBorder: #6b7a74;
  --color-buttonMenuSelectedText: #0f4031;
  --color-buttonMenuSelectedTextHover: #6f501e;
  --color-buttonMenuSelectedBackground: #deccaf;
  --color-buttonMenuSelectedBackgroundHover: #d4b789;
  --color-buttonMenuSelectedBorder: var(--color-buttonMenuSelectedBackground);

  --color-buttonPrimaryText: #ffffff;
  --color-buttonPrimaryTextHover: var(--color-buttonPrimaryText);
  --color-buttonPrimaryBackground: #1c7b5e;
  --color-buttonPrimaryBackgroundHover: #5ea590;
  --color-buttonPrimaryBorder: var(--color-buttonPrimaryBackground);
  --color-buttonPrimaryShadow: rgba(0, 0, 0, 0.3);
  --color-buttonPrimaryDisabledText: #ffffff;
  --color-buttonPrimaryDisabledBackground: #9aa8a3;
  --color-buttonPrimaryDisabledBorder: var(
    --color-buttonPrimaryDisabledBackground
  );

  --color-buttonNormalText: #070d0b;
  --color-buttonNormalTextHover: var(--color-buttonNormalText);
  --color-buttonNormalBackground: #ffffff;
  --color-buttonNormalBackgroundHover: var(--color-buttonNormalBackground);
  --color-buttonNormalBorder: #f2f5f4;
  --color-buttonNormalShadow: rgba(0, 0, 0, 0.2);
  --color-buttonNormalSelectedText: #ffffff;
  --color-buttonNormalSelectedBackground: #177558;
  --color-buttonNormalDisabledText: #9aa8a3;
  --color-buttonNormalDisabledBackground: var(--color-buttonNormalBackground);
  --color-buttonNormalDisabledBorder: var(--color-buttonNormalBorder);

  --color-calendarText: #f7f8f8;
  --color-calendarBackground: #070d0b;
  --color-calendarItemText: #f2f5f4;
  --color-calendarItemBackground: #0d1512;
  --color-calendarSelectedBackground: #6b7a74;
  --color-datePickerRangeBackground: #c1d3cd;

  --color-buttonBareText: var(--color-pageText);
  --color-buttonBareTextHover: var(--color-pageText);
  --color-buttonBareDisabledText: var(--color-pageTextSubdued);
  --color-buttonBareBackground: transparent;
  --color-buttonBareDisabledBackground: var(--color-buttonBareBackground);
  --color-buttonBareBackgroundHover: rgba(100, 100, 100, 0.15);
  --color-buttonBareBackgroundActive: rgba(100, 100, 100, 0.25);

  --color-noticeBackground: #bacec8;
  --color-noticeBackgroundLight: #ecf1f0;
  --color-noticeBackgroundDark: #0f674c;
  --color-noticeText: #104b39;
  --color-noticeTextLight: #0f674c;
  --color-noticeTextDark: #0e3026;
  --color-noticeTextMenu: #74a999;
  --color-noticeBorder: #0f674c;
  --color-warningBackground: #deccaf;
  --color-warningText: #b07d2b;
  --color-warningTextLight: #ca9849;
  --color-warningTextDark: #543d18;
  --color-warningBorder: #ca9849;
  --color-errorBackground: #eddfde;
  --color-errorText: #a93226;
  --color-errorTextDark: #6e241c;
  --color-errorTextDarker: #3c1511;
  --color-errorTextMenu: #d7a49e;
  --color-errorBorder: #a93226;
  --color-upcomingBackground: #eaf0ee;
  --color-upcomingText: #105d46;
  --color-upcomingBorder: #1c7b5e;

  --color-formLabelText: #177558;
  --color-formLabelBackground: #80ab9e;
  --color-formInputBackground: #f7f8f8;
  --color-formInputBackgroundSelected: #ffffff;
  --color-formInputBackgroundSelection: #1c7b5e;
  --color-formInputBorder: #f2f5f4;
  --color-formInputTextReadOnlySelection: #f7f8f8;
  --color-formInputBorderSelected: #1c7b5e;
  --color-formInputText: #070d0b;
  --color-formInputTextSelected: #f7f8f8;
  --color-formInputTextPlaceholder: #9aa8a3;
  --color-formInputTextPlaceholderSelected: #eef1f0;
  --color-formInputTextSelection: #f4f6f5;
  --color-formInputShadowSelected: #5ea590;
  --color-formInputTextHighlight: #86ada1;
  --color-checkboxText: var(--color-tableBackground);
  --color-checkboxBackgroundSelected: #2d876c;
  --color-checkboxBorderSelected: #2d876c;
  --color-checkboxShadowSelected: #5aa58e;
  --color-checkboxToggleBackground: #6d7c76;
  --color-checkboxToggleBackgroundSelected: #0f6b4f;
  --color-checkboxToggleDisabled: #d9dfdd;

  --color-pillBackground: #f2f5f4;
  --color-pillBackgroundLight: #f7f8f8;
  --color-pillText: #0d1512;
  --color-pillTextHighlighted: #0f6b4f;
  --color-pillBorder: #f2f5f4;
  --color-pillBorderDark: #9aa8a3;
  --color-pillBackgroundSelected: #aec6bf;
  --color-pillTextSelected: #104837;
  --color-pillBorderSelected: #1c7b5e;
  --color-pillTextSubdued: #eef1f0;

  --color-reportsRed: #cc7067;
  --color-reportsBlue: #419179;
  --color-reportsGreen: #1d7c5f;
  --color-reportsGray: #6d7c76;
  --color-reportsLabel: #070d0b;
  --color-reportsInnerLabel: #0d1512;
  --color-reportsNumberPositive: var(--color-numberPositive);
  --color-reportsNumberNegative: var(--color-numberNegative);
  --color-reportsNumberNeutral: var(--color-numberNeutral);
  --color-reportsChartFill: var(--color-reportsNumberPositive);

  --color-noteTagBackground: #d2dfdb;
  --color-noteTagBackgroundHover: #c1d3cd;
  --color-noteTagDefault: #d2dfdb;
  --color-noteTagText: #16211d;

  --color-budgetCurrentMonth: var(--color-tableBackground);
  --color-budgetOtherMonth: #f6f8f7;
  --color-budgetHeaderCurrentMonth: var(--color-budgetOtherMonth);
  --color-budgetHeaderOtherMonth: #f5f7f6;

  --color-floatingActionBarBackground: #3f8f77;
  --color-floatingActionBarBorder: var(--color-floatingActionBarBackground);
  --color-floatingActionBarText: #f7f8f8;

  --color-tooltipText: #070d0b;
  --color-tooltipBackground: #ffffff;
  --color-tooltipBorder: #f2f5f4;

  --color-calendarCellBackground: #f4f6f5;

  --color-overlayBackground: rgba(0, 0, 0, 0.3);

  --color-chartQual1: #0f6b4f;
  --color-chartQual2: #b07d2b;
  --color-chartQual3: #5d7f99;
  --color-chartQual4: #a93226;
  --color-chartQual5: #3f8f74;
  --color-chartQual6: #86ad96;
  --color-chartQual7: #6b7a75;
  --color-chartQual8: #ddbc88;
  --color-chartQual9: #365063;
}
